import { useQuery } from '@tanstack/react-query'
import { request } from 'Lib/axios'
import { ExtractFnReturnType } from 'Lib/react-query'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useRef } from 'react'

type GetAccountStatusResp = {
  tagId: number
  active: boolean
}

const getAccountStatus = (
  accountId: number,
  domains = [],
  period = 1
): Promise<{ data: GetAccountStatusResp[] } | Error> => {
  return request<{ data: GetAccountStatusResp[] }>({
    url: `tag-validation/${accountId}/status`,
    method: 'POST',
    data: {
      domains: domains,
      period: period,
    },
  })
}

type QueryFnType = typeof getAccountStatus

export const useAccountStatus = () => {
  const { accountId, subscriptionId } = useUserStore()
  const { setOnboardingModal } = useAppStore()
  const onboardingStateNotSet = useRef(true)
  const onboardingPassed = useRef(false)

  const {
    data: queryData,
    refetch,
    isFetching,
  } = useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: ['getAccountStatus', accountId],
    queryFn: () => getAccountStatus(accountId),
    enabled: !!accountId && !!subscriptionId,
    onSuccess(data) {
      if (!(data instanceof Error)) {
        const hasAtLeastOneDomainActive: boolean =
          Array.isArray(data.data) &&
          data.data.some((obj: GetAccountStatusResp) => obj.active === true)
        // set onboarding modal state only once
        if (onboardingStateNotSet.current) {
          setOnboardingModal(!hasAtLeastOneDomainActive)
          onboardingStateNotSet.current = false
        }
        // if onboarding is passed, we don't need to refetch
        if (!onboardingPassed.current && hasAtLeastOneDomainActive) {
          onboardingPassed.current = hasAtLeastOneDomainActive
        }
      }
    },
    refetchInterval: onboardingPassed ? false : 1000 * 30,
  })

  const data =
    !queryData || queryData instanceof Error
      ? false
      : Array.isArray(queryData.data) &&
        queryData.data.some((obj: GetAccountStatusResp) => obj.active === true)

  return { data, refetch, isFetching }
}
